import { useEffect, useState, useMemo } from "react";
import { fetchWithAuth } from "../api/fetchWithAuth";
import CoverageAoiSelect from "./components/CoverageAoiSelect";
import CoverageSummaryBar from "./components/CoverageSummaryBar";
import CoverageImageryTable from "./components/CoverageImageryTable";

export default function PlatformCoveragePage() {
    const [aoiId, setAoiId] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!aoiId) return;

        setLoading(true);
        setError(null);
        // fetchWithAuth, not bare fetch: this route now requires a platform-admin
        // bearer token, as its siblings on this page always have.
        fetchWithAuth("/api/v1/platform/coverage/imagery", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ coverage_aoi_id: aoiId })
        })
            .then(async r => {
                // The status check is the point. Without it a 401 body parses
                // cleanly into `data`, data.imagery is undefined, and the table
                // renders empty — a failure indistinguishable from an AOI with
                // no imagery. That is how three endpoints 500'd unnoticed for
                // six months, and gating this route without fixing it here
                // would have reproduced it exactly.
                if (!r.ok) {
                    const body = await r.json().catch((e) => { console.error("[ui/src/platform/PlatformCoveragePage.jsx] r.json() failed (swallowed):", e); return {}; });
                    throw new Error(body.error || `HTTP ${r.status}`);
                }
                return r.json();
            })
            .then(setData)
            .catch(err => {
                console.error("[coverage] imagery fetch failed", err);
                setError(err?.message || "Failed to load imagery coverage");
                setData(null);
            })
            .finally(() => setLoading(false));
    }, [aoiId]);

    // DERIVED DATA (VISIBLE TO RENDER)
    const sortedImagery = useMemo(() => {
        if (!data?.imagery) return [];

        const rank = { FULL: 0, PARTIAL: 1, NONE: 2 };

        return data.imagery
            .slice()
            .sort((a, b) => {
                return (
                    rank[a.coverage_status] - rank[b.coverage_status] ||
                    b.coverage_pct - a.coverage_pct ||
                    new Date(b.acquisition_date || 0) -
                    new Date(a.acquisition_date || 0)
                );
            });
    }, [data]);

    return (
        <div className="p-6 space-y-4">
            <h1 className="text-sm font-semibold">
                Platform AOI → Imagery Coverage
            </h1>

            <CoverageAoiSelect onChange={setAoiId} />

            {error && (
                <div className="text-[12px] text-rose-300 border border-rose-500/40 bg-rose-500/10 rounded px-3 py-2">
                    Imagery coverage unavailable — {error}
                </div>
            )}

            {loading && <div className="text-muted">Loading coverage…</div>}

            {data && (
                <>
                    <CoverageSummaryBar summary={data.summary} />
                    <CoverageImageryTable imagery={sortedImagery} />
                </>
            )}
        </div>
    );
}